export type ResourceAbilityDecision = {
  allow: boolean;
  deny: boolean;
  isTerminal?: boolean; // True if this key was an explicit endpoint of an ability
};

export type ResourceAbilityIndex = Map<string, ResourceAbilityDecision>;

const ACTIONS = new Set(["access", "read"]);
const EFFECTS = new Set(["grant", "deny"]);

/**
 * Strip the module prefix (e.g. "RXDV1.") from an ability string.
 * Returns the remainder for resource key parsing.
 */
export function toModuleRelativeAbility(
  ability: string,
  modulePrefix: string
): string {
  if (!ability) return "";
  const normalized = ability.trim();
  if (!normalized) return "";

  const prefix = modulePrefix ? `${modulePrefix}.` : "";
  if (prefix && normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length);
  }

  return normalized;
}

function parseExplicitResourceDecision(relativeAbilityLower: string): {
  resourceKey: string;
  deny: boolean;
  allow: boolean;
} | null {
  const parts = relativeAbilityLower.split(".").filter(Boolean);
  if (parts.length < 3) return null;

  const effect = parts[parts.length - 1];
  const action = parts[parts.length - 2];
  if (!EFFECTS.has(effect) || !ACTIONS.has(action)) return null;

  let resourceParts: string[];
  const maybeAll = parts[parts.length - 3];
  if (maybeAll === "all") {
    resourceParts = parts.slice(0, parts.length - 3);
  } else {
    resourceParts = parts.slice(0, parts.length - 2);
  }

  const resourceKey = resourceParts.join(".");
  const isDeny = effect === "deny";
  const isAllow = effect === "grant";
  return { resourceKey, deny: isDeny, allow: isAllow };
}

/**
 * After building the index, propagate allows UP to ancestor prefixes.
 * e.g. if "provider.pharmacynetwork.networksearch" has allow,
 * then "provider.pharmacynetwork" and "provider" also get allow.
 * Deny is NOT propagated (stays scoped to its exact key).
 */
function propagateAllowsUp(index: ResourceAbilityIndex): void {
  const keysWithAllow = [...index.entries()]
    .filter(([, decision]) => decision.allow)
    .map(([key]) => key);

  for (const key of keysWithAllow) {
    const parts = key.split(".");
    for (let i = parts.length - 1; i >= 1; i--) {
      const ancestor = parts.slice(0, i).join(".");
      const prev = index.get(ancestor) ?? { allow: false, deny: false };
      if (!prev.allow) {
        index.set(ancestor, { ...prev, allow: true });
      }
    }
  }
}

/**
 * Build an index of resource keys → allow/deny from a flat list of ability strings.
 * Only requires modulePrefix (e.g. "RXDV1") to strip before parsing.
 * Marks keys that were explicit endpoints (isTerminal) vs propagated.
 */
export function buildResourceAbilityIndex(
  abilities: string[],
  modulePrefix: string
): ResourceAbilityIndex {
  const index: ResourceAbilityIndex = new Map();

  for (const ability of abilities ?? []) {
    const relative = toModuleRelativeAbility(ability, modulePrefix);
    if (!relative) continue;

    const parsed = parseExplicitResourceDecision(relative.toLowerCase());
    if (!parsed) continue;

    const prev = index.get(parsed.resourceKey) ?? { allow: false, deny: false };
    index.set(parsed.resourceKey, {
      allow: prev.allow || parsed.allow,
      deny: prev.deny || parsed.deny,
      isTerminal: true, // Mark as explicit endpoint of an ability
    });
  }

  propagateAllowsUp(index);
  return index;
}

/**
 * Determines menu visibility for a resource key.
 *
 * Visibility requires:
 * 1. No explicit deny at this key or any ancestor (denies cascade up)
 * 2. Explicit allow at:
 *    - This EXACT key, OR
 *    - An ancestor that is explicitly terminal (from ability endpoint, not propagated)
 *
 * This allows:
 * - `batchprocess.lists.all` to grant all children under `batchprocess.lists`
 * - But `batchprocess.lists.gpilists.all` only grants to gpilists and children, not siblings
 */
export function canSeeResourceMenuItem(
  index: ResourceAbilityIndex,
  resourceKey: string
): boolean {
  if (!resourceKey) return false;
  const normalized = resourceKey.trim().toLowerCase();
  if (!normalized) return false;

  const parts = normalized.split(".").filter(Boolean);

  // Check for deny at this key or any ancestor (denies cascade up)
  for (let i = parts.length; i >= 1; i--) {
    const candidate = parts.slice(0, i).join(".");
    const decision = index.get(candidate);
    if (decision?.deny) return false;
  }

  // Check for allow at this key or ancestors that were explicitly terminal
  for (let i = parts.length; i >= 1; i--) {
    const candidate = parts.slice(0, i).join(".");
    const decision = index.get(candidate);
    if (decision?.allow) {
      // Allow if exact match or if this is a terminal (explicit endpoint) grant
      if (candidate === normalized || decision.isTerminal) {
        return true;
      }
    }
  }

  return false;
}
